import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, User, FileText, Camera, PenTool, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * IDProofingDashboard Component
 * 
 * Dashboard interface for physician ID verification with independent steps:
 * - NPI Verification
 * - Driver's License Upload  
 * - Identity Photo (Selfie)
 * - Electronic Signature
 * - Final Review & Submission
 * 
 * Features:
 * - Complete steps in any order
 * - Visual progress tracking
 * - Individual step persistence
 * - Mobile responsive design
 */

interface IDProofingDashboardProps {
  physicianId: string;
  initialNPI?: string;
  onComplete: () => void;
  onCancel: () => void;
}

interface StepStatus {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'in_progress' | 'completed';
  route: string;
  estimatedTime: string;
}

interface IDProofingData {
  npi: string;
  driversLicense: File | null;
  selfiePhoto: Blob | null;
  signature: Blob | null;
  stepStatuses: Record<string, 'pending' | 'in_progress' | 'completed'>;
  lastUpdated: number;
}

export const IDProofingDashboard: React.FC<IDProofingDashboardProps> = ({
  physicianId,
  initialNPI = '',
  onComplete: _onComplete,
  onCancel
}) => {
  const navigate = useNavigate();
  const STORAGE_KEY = `idproofing_data_${physicianId}`;

  // Backend status state
  const [backendStatus, setBackendStatus] = useState<{
    status: string;
    isVerified: boolean;
    submittedAt?: string;
    verifiedAt?: string;
  } | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Load saved data from localStorage
  const loadSavedData = (): IDProofingData => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only use saved data if less than 7 days old
        if (parsed.lastUpdated && (Date.now() - parsed.lastUpdated) < 7 * 24 * 60 * 60 * 1000) {
          return {
            ...parsed,
            // Don't restore file objects for security
            driversLicense: null,
            selfiePhoto: null,
            signature: null
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load saved ID proofing data:', error);
    }
    
    return {
      npi: initialNPI,
      driversLicense: null,
      selfiePhoto: null,
      signature: null,
      stepStatuses: {
        npi: initialNPI ? 'completed' : 'pending',
        license: 'pending',
        selfie: 'pending',
        signature: 'pending'
      },
      lastUpdated: Date.now()
    };
  };

  const [idProofingData, setIdProofingData] = useState<IDProofingData>(loadSavedData);

  // Fetch backend status on component mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setStatusLoading(true);
        const response = await apiClient.getIdProofingStatus(physicianId);
        if (response.success && response.data) {
          setBackendStatus({
            status: response.data.idProofingStatus,
            isVerified: response.data.isVerified,
            submittedAt: response.data.submittedAt,
            verifiedAt: response.data.verifiedAt
          });
          
          // If submitted/approved/rejected, clear local storage since workflow is complete
          if (['submitted', 'approved', 'rejected'].includes(response.data.idProofingStatus)) {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Failed to fetch ID proofing status:', error);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchStatus();
  }, [physicianId, STORAGE_KEY]);

  // Save data to localStorage whenever it changes (only if not submitted)
  useEffect(() => {
    if (backendStatus?.status && !['submitted', 'verified', 'rejected'].includes(backendStatus.status)) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          ...idProofingData,
          lastUpdated: Date.now()
        }));
      } catch (error) {
        console.warn('Failed to save ID proofing data:', error);
      }
    }
  }, [idProofingData, STORAGE_KEY, backendStatus?.status]);

  // Define the verification steps
  const steps: StepStatus[] = [
    {
      id: 'npi',
      title: 'NPI Verification',
      description: 'Confirm your National Provider Identifier number',
      icon: <User className="h-6 w-6" />,
      status: idProofingData.stepStatuses.npi,
      route: '/id-proofing/npi',
      estimatedTime: '2 min'
    },
    {
      id: 'license',
      title: 'ID Document Upload',
      description: 'Upload your driver\'s license or state ID',
      icon: <FileText className="h-6 w-6" />,
      status: idProofingData.stepStatuses.license,
      route: '/id-proofing/license',
      estimatedTime: '3 min'
    },
    {
      id: 'selfie',
      title: 'Identity Photo',
      description: 'Take a selfie for identity verification',
      icon: <Camera className="h-6 w-6" />,
      status: idProofingData.stepStatuses.selfie,
      route: '/id-proofing/selfie',
      estimatedTime: '2 min'
    },
    {
      id: 'signature',
      title: 'Digital Signature',
      description: 'Provide your electronic signature',
      icon: <PenTool className="h-6 w-6" />,
      status: idProofingData.stepStatuses.signature,
      route: '/id-proofing/signature',
      estimatedTime: '2 min'
    }
  ];

  // Calculate completion progress
  const completedSteps = steps.filter(step => step.status === 'completed').length;
  const totalSteps = steps.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;
  const allStepsCompleted = completedSteps === totalSteps;

  // Check if a step can be accessed based on prerequisites
  const canAccessStep = (stepId: string): boolean => {
    const stepOrder = ['npi', 'license', 'selfie', 'signature'];
    const stepIndex = stepOrder.indexOf(stepId);
    
    // Always allow first step
    if (stepIndex === 0) return true;
    
    // Check if all previous steps are completed
    for (let i = 0; i < stepIndex; i++) {
      const previousStep = stepOrder[i];
      if (idProofingData.stepStatuses[previousStep] !== 'completed') {
        return false;
      }
    }
    
    return true;
  };

  // Get prerequisite message for locked steps
  const getPrerequisiteMessage = (stepId: string): string => {
    const stepOrder = ['npi', 'license', 'selfie', 'signature'];
    const stepIndex = stepOrder.indexOf(stepId);
    
    if (stepIndex <= 0) return '';
    
    const incompleteSteps = [];
    for (let i = 0; i < stepIndex; i++) {
      const previousStep = stepOrder[i];
      if (idProofingData.stepStatuses[previousStep] !== 'completed') {
        const stepNames: Record<string, string> = {
          npi: 'NPI Verification',
          license: 'ID Document Upload',
          selfie: 'Identity Photo'
        };
        incompleteSteps.push(stepNames[previousStep] || previousStep);
      }
    }
    
    if (incompleteSteps.length === 1) {
      return `Complete ${incompleteSteps[0]} first`;
    } else if (incompleteSteps.length > 1) {
      return `Complete ${incompleteSteps.slice(0, -1).join(', ')} and ${incompleteSteps.slice(-1)} first`;
    }
    
    return '';
  };

  // Handle step click
  const handleStepClick = (step: StepStatus) => {
    // Check if step can be accessed
    if (!canAccessStep(step.id)) {
      // Don't navigate - step is locked
      return;
    }

    // Mark step as in progress
    setIdProofingData(prev => ({
      ...prev,
      stepStatuses: {
        ...prev.stepStatuses,
        [step.id]: step.status === 'pending' ? 'in_progress' : step.status
      }
    }));
    
    navigate(step.route);
  };

  // Handle final submission
  const handleFinalSubmission = () => {
    if (allStepsCompleted) {
      navigate('/id-proofing/review');
    }
  };

  // Get status color for step cards
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'in_progress':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-blue-600" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  // Show loading state
  if (statusLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading verification status...</p>
        </div>
      </div>
    );
  }

  // Show status-specific views for submitted/verified/rejected
  if (backendStatus?.status === 'submitted') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <div className="mb-6">
              <Clock className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Submitted</h1>
              <p className="text-gray-600">Your ID verification has been submitted for review</p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-1 text-left">
                <li>• Our verification team will review your documents</li>
                <li>• This process typically takes 1-2 business days</li>
                <li>• You'll receive an email notification when complete</li>
                <li>• You can check your status anytime in your dashboard</li>
              </ul>
            </div>

            {backendStatus.submittedAt && (
              <p className="text-sm text-gray-500 mb-6">
                Submitted: {formatDateTime(backendStatus.submittedAt, { month: 'long' })}
              </p>
            )}

            <button
              onClick={onCancel}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (backendStatus?.status === 'verified') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <div className="mb-6">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Complete</h1>
              <p className="text-gray-600">Your physician identity has been verified successfully!</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-green-900 mb-2">🎉 Congratulations!</h3>
              <p className="text-sm text-green-800">
                You can now access all physician features and begin ordering medical products for your patients.
              </p>
            </div>

            {backendStatus.verifiedAt && (
              <p className="text-sm text-gray-500 mb-6">
                Verified: {formatDateTime(backendStatus.verifiedAt, { month: 'long' })}
              </p>
            )}

            <button
              onClick={onCancel}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (backendStatus?.status === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <div className="mb-6">
              <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Needs Attention</h1>
              <p className="text-gray-600">Your verification requires additional information or corrections</p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-red-900 mb-2">What to do next</h3>
              <p className="text-sm text-red-800 mb-3">
                Please contact support or check your email for specific instructions on how to complete your verification.
              </p>
              <div className="text-left">
                <p className="text-xs text-red-700">Common issues:</p>
                <ul className="text-xs text-red-700 space-y-1 mt-1">
                  <li>• Document image quality needs improvement</li>
                  <li>• Additional documentation required</li>
                  <li>• Information mismatch requiring clarification</li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-3 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Contact Support
              </button>
              <button
                onClick={onCancel}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Physician ID Verification</h1>
              <p className="text-gray-600 mt-1">Complete the verification steps below in any order</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 px-3 py-1 rounded"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress: {completedSteps} of {totalSteps} steps completed
              </span>
              <span className="text-sm text-gray-500">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {steps.map((step) => {
            const isAccessible = canAccessStep(step.id);
            const prerequisiteMessage = getPrerequisiteMessage(step.id);
            
            return (
              <div
                key={step.id}
                onClick={() => handleStepClick(step)}
                className={`
                  ${getStatusColor(step.status)}
                  border-2 rounded-lg p-6 transition-all duration-200
                  ${isAccessible 
                    ? 'cursor-pointer hover:shadow-md hover:scale-105 active:scale-95' 
                    : 'cursor-not-allowed opacity-60'
                  }
                  ${!isAccessible ? 'border-gray-300 bg-gray-50' : ''}
                `}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`
                    p-3 rounded-full ${
                      !isAccessible ? 'bg-gray-100 text-gray-400' :
                      step.status === 'completed' ? 'bg-green-100 text-green-600' :
                      step.status === 'in_progress' ? 'bg-blue-100 text-blue-600' :
                      'bg-gray-100 text-gray-400'
                    }
                  `}>
                    {step.icon}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isAccessible ? (
                      <Circle className="h-5 w-5 text-gray-400" />
                    ) : (
                      getStatusIcon(step.status)
                    )}
                    <ArrowRight className={`h-4 w-4 ${isAccessible ? 'text-gray-400' : 'text-gray-300'}`} />
                  </div>
                </div>
                
                <h3 className={`text-lg font-semibold mb-2 ${isAccessible ? 'text-gray-900' : 'text-gray-500'}`}>
                  {step.title}
                </h3>
                
                {!isAccessible && prerequisiteMessage ? (
                  <div className="mb-3">
                    <p className="text-gray-600 text-sm mb-2">
                      {step.description}
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-yellow-800 text-xs font-medium">
                        🔒 {prerequisiteMessage}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm mb-3">
                    {step.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isAccessible ? 'text-gray-500' : 'text-gray-400'}`}>
                    Est. time: {step.estimatedTime}
                  </span>
                  <span className={`
                    text-xs px-2 py-1 rounded-full ${
                      !isAccessible ? 'bg-gray-100 text-gray-500' :
                      step.status === 'completed' ? 'bg-green-100 text-green-700' :
                      step.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }
                  `}>
                    {!isAccessible ? 'Locked' :
                     step.status === 'completed' ? 'Completed' :
                     step.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Final Submission */}
        {allStepsCompleted && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              All Steps Completed!
            </h3>
            <p className="text-green-700 mb-4">
              You've completed all verification steps. Click below to review and submit your information.
            </p>
            <button
              onClick={handleFinalSubmission}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
              Review & Submit for Verification
            </button>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Getting Started</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Complete the verification steps in any order that works for you</li>
            <li>• Your progress is automatically saved as you go</li>
            <li>• You can return anytime to complete remaining steps</li>
            <li>• All steps must be completed before final submission</li>
          </ul>
        </div>
      </div>
    </div>
  );
}; 